import { useCallback, useEffect, useMemo, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CollaborativeEditor } from "@/components/editors/CollaborativeEditor";
import {
  useCreateGlobalDocument,
  useCreateGlobalDocumentLink,
  useGlobalDocumentLinksByEntity,
  useGlobalDocuments,
} from "@/hooks/useGlobalDocuments";
import { SIM_DOCS_LINK_ID } from "@/lib/sims";

/**
 * Shared team docs about SIM cards (procedures, provider contacts, …). One
 * collaborative global document, hidden from the Docs tree and found through
 * its `sim_cards` link, like the project notes. The migration seeds it; it is
 * created here only if missing (the tab content mounts only while shown).
 */
export function SimDocsPanel() {
  const { data: docs, isLoading: docsLoading } = useGlobalDocuments(true);
  const { data: links, isLoading: linksLoading } =
    useGlobalDocumentLinksByEntity("sim_cards", SIM_DOCS_LINK_ID);
  const createDoc = useCreateGlobalDocument();
  const createLink = useCreateGlobalDocumentLink();
  const autoCreateStarted = useRef(false);

  // Oldest linked document wins if two were ever created concurrently.
  const doc = useMemo(() => {
    const linkedIds = new Set(links.map((link) => link.global_document_id));
    return docs
      .filter((d) => linkedIds.has(d.id) && !d.is_folder && d.document_id)
      .sort((a, b) => a.created_at.localeCompare(b.created_at))[0];
  }, [docs, links]);

  const create = useCallback(async () => {
    const created = await createDoc.mutateAsync({
      title: "SIM Cards",
      is_folder: false,
      hidden_from_docs: true,
    });
    await createLink.mutateAsync({
      global_document_id: created.id,
      link_type: "sim_cards",
      link_id: SIM_DOCS_LINK_ID,
    });
  }, [createDoc, createLink]);

  useEffect(() => {
    if (docsLoading || linksLoading || doc) return;
    if (autoCreateStarted.current) return;
    autoCreateStarted.current = true;
    void create();
  }, [create, doc, docsLoading, linksLoading]);

  return (
    <div className="space-y-3" data-testid="sim-docs">
      <p className="text-sm text-muted-foreground">
        Team notes about SIM cards: procedures, provider contacts, APN and
        pricing details. Changes are saved automatically and shared with
        everyone.
      </p>
      {doc ? (
        <Card>
          <CardContent className="p-4" data-testid="sim-docs-editor">
            <CollaborativeEditor
              documentId={doc.document_id}
              editable
              placeholder="Start writing SIM card docs…"
            />
          </CardContent>
        </Card>
      ) : (
        <Skeleton className="h-48 w-full" />
      )}
    </div>
  );
}
